const USERNAME_KEY = "username";
const ACCESS_KEY = "access";
const REFRESH_KEY = "refresh";

const parseJwt = (token) => {
    if (!token) {
        return null;
    }
    const parts = token.split(".");
    if (parts.length < 2) {
        return null;
    }
    try {
        let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padLength = payload.length % 4;
        if (padLength) {
            payload += "=".repeat(4 - padLength);
        }
        const decoded = atob(payload);
        return JSON.parse(decoded);
    } catch (error) {
        return null;
    }
};

const getTokenExpiry = (token) => {
    const payload = parseJwt(token);
    if (!payload || typeof payload.exp !== "number") {
        return null;
    }
    return payload.exp * 1000;
};

export const isTokenExpired = (token, skewSeconds = 30) => {
    const expiry = getTokenExpiry(token);
    if (!expiry) {
        return true;
    }
    return Date.now() >= expiry - skewSeconds * 1000;
};

export const getAccessToken = () => localStorage.getItem(ACCESS_KEY);

export class UnauthenticatedError extends Error {
    constructor() {
        super("Unauthenticated");
        this.name = "UnauthenticatedError";
    }
}

/**
 * 인증 상태를 확인하는 순수 predicate. side-effect 없음.
 * access/refresh 토큰 유무 및 만료 여부로 4가지 케이스를 분기한다.
 */
export const isAuthenticated = () => {
    const access = getAccessToken();
    const refresh = getRefreshToken();
    if (!access && !refresh) return false;
    if (refresh && isTokenExpired(refresh, 0)) return false;
    if (!refresh && access && isTokenExpired(access, 0)) return false;
    return true;
};

/**
 * 미인증 시 UnauthenticatedError를 throw한다. side-effect(redirect/clearSession) 없음.
 * redirect 동작이 필요하면 requireLogin()을 사용할 것.
 */
export const requireLoginOrThrow = () => {
    // eslint-disable-next-line no-restricted-syntax -- 정의부 내부 호출(가드 로직 자체), componentDidMount 규칙 대상 아님
    if (!isAuthenticated()) throw new UnauthenticatedError();
};

/**
 * requireLoginOrThrow()의 try/catch wrapper. 기존 boolean 반환 + redirect 동작을 유지한다.
 * 미인증 시 clearLoginSession() 호출 후 /login으로 redirect하고 false를 반환한다.
 *
 * @deprecated Use requireLoginOrThrow() in componentDidMount. Scheduled for removal in DAS-64+.
 */
export const requireLogin = () => {
    try {
        // eslint-disable-next-line no-restricted-syntax -- try/catch wrapper 내부 호출(정의부 자체), componentDidMount 규칙 대상 아님
        requireLoginOrThrow();
        return true;
    } catch (e) {
        if (!(e instanceof UnauthenticatedError)) {
            throw e;
        }
        markSessionExpired();
        clearLoginSession();
        window.location.replace("/login");
        return false;
    }
};

export const getStoredUsername = () => localStorage.getItem(USERNAME_KEY);

export const setLoginSession = (username, accessToken, refreshToken) => {
    localStorage.setItem(ACCESS_KEY, accessToken);
    localStorage.setItem(USERNAME_KEY, username);
    if (refreshToken) {
        localStorage.setItem(REFRESH_KEY, refreshToken);
    }
};

export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

export const setAccessToken = (accessToken) => {
    if (accessToken) {
        localStorage.setItem(ACCESS_KEY, accessToken);
    }
};

export const clearLoginSession = () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USERNAME_KEY);
    // 서버 httpOnly refresh 쿠키도 제거 (best-effort) — 로그아웃이 콜드 스타트
    // bootstrap 으로 되살아나지 않도록. 동일 origin 상대경로 → 쿠키 동봉.
    try {
        if (typeof fetch === "function") {
            fetch("/api/user/token/logout", {
                method: "POST",
                credentials: "include",
                keepalive: true,
            }).catch(() => {});
        }
    } catch (e) {
        /* ignore */
    }
};

export const markSessionExpired = () => {
    localStorage.setItem("session_expired", "true");
};

export const consumeSessionExpired = () => {
    const flag = localStorage.getItem("session_expired");
    if (flag) {
        localStorage.removeItem("session_expired");
        return true;
    }
    return false;
};
